// Resumable multipart uploader for course/event recordings, wired to R2
// via the /api/v1/teachers/recordings/* endpoints. Uppy handles chunking,
// parallelism, retries, and pause/resume; we just wire the four lifecycle
// callbacks (createMultipartUpload, signPart, completeMultipartUpload,
// abortMultipartUpload) to our Rails endpoints.
//
// This component is the upload form ONLY. The parent owns the list of
// existing recordings; we fire `onUploaded(recording)` when a new one
// finishes.
import React, { useEffect, useMemo, useRef, useState } from 'react'
import Uppy from '@uppy/core'
import AwsS3Multipart from '@uppy/aws-s3-multipart'
import { DragDrop, StatusBar } from '@uppy/react'

import '@uppy/core/dist/style.min.css'
import '@uppy/drag-drop/dist/style.min.css'
import '@uppy/status-bar/dist/style.min.css'

const csrfToken = () =>
  (document.head.querySelector('[name~=csrf-token]') || {}).content

const jsonHeaders = () => ({
  'Content-Type': 'application/json',
  'X-CSRF-TOKEN': csrfToken(),
  Accept: 'application/json',
  'X-Requested-With': 'XMLHttpRequest'
})

const MAX_FILE_SIZE = 10 * 1024 * 1024 * 1024 // 10GB

const RecordingUploader = ({ eventId, label, onUploaded }) => {
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)

  // Refs so the Uppy plugin callbacks (built once via useMemo) always read
  // the latest label/handlers without rebuilding the Uppy instance, which
  // would tear down the DragDrop UI on every keystroke.
  const labelRef = useRef(label)
  const onUploadedRef = useRef(onUploaded)
  useEffect(() => { labelRef.current = label }, [label])
  useEffect(() => { onUploadedRef.current = onUploaded }, [onUploaded])

  const uppy = useMemo(() => {
    const u = new Uppy({
      autoProceed: true,
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['video/*'],
        maxFileSize: MAX_FILE_SIZE
      }
    })

    u.use(AwsS3Multipart, {
      limit: 4,
      async createMultipartUpload (file) {
        const res = await fetch('/api/v1/teachers/recordings/initiate', {
          method: 'POST',
          credentials: 'same-origin',
          headers: jsonHeaders(),
          body: JSON.stringify({
            filename: file.name,
            title: file.name,
            label: labelRef.current,
            event_id: eventId,
            content_type: file.type || 'video/mp4',
            file_size: file.size
          })
        })
        if (!res.ok) throw new Error(`initiate ${res.status}`)
        const data = await res.json()
        u.setFileMeta(file.id, { recordingId: data.recording_id })
        return { uploadId: data.upload_id, key: data.key }
      },
      async signPart (file, { partNumber }) {
        const recordingId = file.meta.recordingId
        const res = await fetch(`/api/v1/teachers/recordings/${recordingId}/sign_part`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: jsonHeaders(),
          body: JSON.stringify({ part_number: partNumber })
        })
        if (!res.ok) throw new Error(`sign_part ${res.status}`)
        const { url } = await res.json()
        return { url }
      },
      async completeMultipartUpload (file, { parts }) {
        const recordingId = file.meta.recordingId
        const res = await fetch(`/api/v1/teachers/recordings/${recordingId}/complete`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: jsonHeaders(),
          body: JSON.stringify({
            parts: parts.map(p => ({ part_number: p.PartNumber, etag: p.ETag }))
          })
        })
        if (!res.ok) throw new Error(`complete ${res.status}`)
        const data = await res.json()
        return { location: '', recording: data.recording }
      },
      async abortMultipartUpload (file) {
        const recordingId = file.meta.recordingId
        if (!recordingId) return
        await fetch(`/api/v1/teachers/recordings/${recordingId}/abort`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: jsonHeaders()
        })
      }
    })

    return u
  }, [eventId])

  useEffect(() => {
    const onUploadProgress = (file, p) => {
      if (!p.bytesTotal) return
      setProgress(Math.round((p.bytesUploaded / p.bytesTotal) * 100))
    }
    const onComplete = (result) => {
      const file = result.successful[0]
      if (!file) return
      const data = file.response?.body?.recording
      const built = data || {
        id: file.meta.recordingId,
        title: file.name,
        label: labelRef.current,
        fileSizeBytes: file.size,
        status: 'ready'
      }
      setProgress(0)
      setError(null)
      // Tell parent first so it can update its list, then reset Uppy so the
      // same dropzone can accept the next file.
      onUploadedRef.current && onUploadedRef.current(built)
      uppy.cancelAll()
    }
    const onUploadError = (_file, err) => setError(err?.message || 'Upload failed.')

    uppy.on('upload-progress', onUploadProgress)
    uppy.on('complete', onComplete)
    uppy.on('upload-error', onUploadError)
    return () => {
      uppy.off('upload-progress', onUploadProgress)
      uppy.off('complete', onComplete)
      uppy.off('upload-error', onUploadError)
    }
  }, [uppy])

  useEffect(() => () => uppy.close(), [uppy])

  return (
    <div>
      <DragDrop
        uppy={uppy}
        locale={{
          strings: {
            dropHereOr: 'Drop video here or %{browse}',
            browse: 'browse'
          }
        }}
      />
      <StatusBar uppy={uppy} hideUploadButton hideCancelButton showProgressDetails />
      {progress > 0 && progress < 100 && (
        <div className='mt-1 text-xs text-gray-500'>{progress}% uploaded</div>
      )}
      {error && <div className='mt-2 text-sm text-red-600'>{error}</div>}
    </div>
  )
}

export default RecordingUploader
